/**
 * Everything the search did that the user did not literally ask for.
 *
 * The rule these all share: when the system widens, narrows or rewrites a
 * query, it says so and offers the way back, rather than quietly doing it on the
 * user's behalf. An agent staring at an empty-looking screen has no way to tell
 * a strict filter from a genuinely empty catalogue.
 */
export function Notices({ data, filters, options, dispatch, onSearchExact }) {
  const notices = [];

  /**
   * Two different causes, and they are not the user's problem in the same way.
   *
   * `field-not-sortable` is the index being unable to run a real sort — it
   * passes, and "right now" is honest. `unknown-sort-key` means the sort key
   * this client sent is not one the API has, which is a bug here and not
   * something waiting will fix; saying "right now" would send someone to check
   * Typesense for a typo in this file. The API used to report neither: an
   * unknown key came back as a clean 200 sorted by relevance.
   */
  if (data?.sortDegraded) {
    notices.push(data.sort?.reason === 'unknown-sort-key'
      ? <span key="sort">{`“${data.sort.requested}” isn’t a sort option — showing best match instead.`}</span>
      : <span key="sort">That sort option isn’t available right now — showing best match instead.</span>);
  }

  /**
   * The intake filter is strict: asking for an exact intake returns only
   * courses we know run in it. But 45,674 of 86,458 courses have no resolvable
   * intake at all, so a strict filter hides more than half the catalogue.
   *
   * The count is catalogue-wide, not from this response: with the intake filter
   * applied every remaining course has a known intake, so the facet reads 0.
   */
  if (filters.intakeIds.length && !filters.includeUnknownIntake) {
    const unknown = options?.intakeUnknown ?? 0;
    if (unknown > 0) {
      notices.push(
        <span key="intake">
          {`${unknown.toLocaleString()} courses have no intake date recorded and are excluded by this filter. `}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              dispatch({ type: 'SET_FLAG', key: 'includeUnknownIntake', value: true });
            }}
          >
            Include them
          </a>
          .
        </span>,
      );
    }
  }

  /**
   * The same rule as intake, for the same reason: 22.8% of the catalogue records
   * no English requirement at all, and a strict filter hides all of it.
   *
   * Catalogue-wide count, not from this response - once the English filter is
   * applied every remaining course has a known test, so the facet reads 0.
   */
  if (filters.englishTests.length && !filters.includeUnknownEnglish) {
    const unknown = options?.englishUnknown ?? 0;
    if (unknown > 0) {
      notices.push(
        <span key="english">
          {`${unknown.toLocaleString()} courses have no English requirement recorded and are excluded by this filter. `}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              dispatch({ type: 'SET_FLAG', key: 'includeUnknownEnglish', value: true });
            }}
          >
            Include them
          </a>
          .
        </span>,
      );
    }
  }

  // A completed half-word or an expanded abbreviation changes which courses come
  // back, so it is stated rather than applied behind the user's back — the same
  // reasoning as the intake notice. The way out is to search the exact wording,
  // so the searched-for text is shown and can be put back.
  const q = data?.query;

  if (q?.expansions?.length) {
    const from = q.expansions.map((e) => e.from).join(', ');
    notices.push(
      <span key="expansion">
        {'Searched for '}<b>{q.sent}</b>{`, reading ${from} as a shortened word. `}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); onSearchExact(); }}
        >
          {`Search “${filters.q.trim()}” exactly`}
        </a>
        .
      </span>,
    );
  }

  if (q?.note === 'symbol-only') {
    notices.push(
      <span key="symbol">
        {'Symbols aren’t indexed, so this searched for '}<b>{q.sent}</b>
        {'. A term like “C++” can’t be told apart from “C” in the catalogue.'}
      </span>,
    );
  }

  if (q?.truncated) {
    notices.push(
      <span key="truncated">That query was very long, so only the first few words were searched.</span>,
    );
  }

  if (notices.length === 0) return null;

  return (
    <div id="notice">
      {notices.map((n) => <div className="notice" key={n.key}>{n}</div>)}
    </div>
  );
}
